import { useState, useEffect } from "react";
import { Plus, NotebookPen, Trash2, Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { Button } from "@/components/Button";
import { Modal, Field, TextInput, TextArea } from "@/components/Modal";
import { EmptyState, Loader } from "@/components/common";

export function NotesSection({ cid }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get(`/connections/${cid}/notes`); setItems(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cid]);

  const startAdd = () => { setEditing(null); setForm({ title: "", content: "", checklist: [] }); setOpen(true); };
  const startEdit = (n) => { setEditing(n); setForm({ ...n, checklist: n.checklist || [] }); setOpen(true); };

  const save = async () => {
    if (!form.title?.trim()) return toast.error("Give your note a title.");
    setBusy(true);
    try {
      const payload = { title: form.title, content: form.content || "", checklist: form.checklist || [] };
      if (editing) await api.put(`/connections/${cid}/notes/${editing.id}`, payload);
      else await api.post(`/connections/${cid}/notes`, payload);
      setOpen(false); await load(); toast.success("Saved");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const del = async (n) => { if (confirm("Delete this note?")) { await api.delete(`/connections/${cid}/notes/${n.id}`); await load(); } };

  const toggleCheck = async (n, idx) => {
    const checklist = n.checklist.map((c, i) => (i === idx ? { ...c, done: !c.done } : c));
    await api.put(`/connections/${cid}/notes/${n.id}`, { title: n.title, content: n.content, checklist });
    await load();
  };

  if (loading) return <Loader />;

  return (
    <div>
      <div className="flex justify-end mb-4"><Button onClick={startAdd} data-testid="add-note-btn"><Plus size={16} /> Add Note</Button></div>
      {items.length === 0 ? (
        <EmptyState icon={NotebookPen} title="No notes or plans yet" subtitle="Jot down shared plans, lists, and ideas together." action={<Button onClick={startAdd}><Plus size={16} /> Add Note</Button>} testId="notes-empty" />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {items.map((n) => (
            <div key={n.id} className="iks-card p-4 group" data-testid="note-card">
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-display font-semibold text-slate-50">{n.title}</h3>
                <div className="flex gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => startEdit(n)} className="p-1 text-slate-400 hover:text-sky-400"><Pencil size={14} /></button>
                  <button onClick={() => del(n)} className="p-1 text-slate-400 hover:text-red-400"><Trash2 size={14} /></button>
                </div>
              </div>
              {n.content && <p className="text-slate-300 text-sm mt-1.5 whitespace-pre-wrap">{n.content}</p>}
              {n.checklist?.length > 0 && (
                <div className="mt-3 space-y-1.5">
                  {n.checklist.map((c, i) => (
                    <button key={i} onClick={() => toggleCheck(n, i)} className="flex items-center gap-2 text-sm w-full text-left">
                      <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${c.done ? "bg-sky-500 border-sky-500" : "border-slate-600"}`}>
                        {c.done && <Check size={11} className="text-white" />}
                      </span>
                      <span className={c.done ? "line-through text-slate-500" : "text-slate-300"}>{c.text}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit note" : "Add note"} testId="note-modal"
        footer={<><Button variant="secondary" full onClick={() => setOpen(false)}>Cancel</Button><Button full loading={busy} onClick={save} data-testid="save-note-btn">Save</Button></>}>
        <Field label="Title"><TextInput value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="Vacation Plans" data-testid="note-title" /></Field>
        <Field label="Content"><TextArea value={form.content} onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))} placeholder="Write anything…" /></Field>
        <Field label="Checklist">
          <div className="space-y-2">
            {(form.checklist || []).map((c, i) => (
              <div key={i} className="flex items-center gap-2">
                <TextInput value={c.text} onChange={(e) => setForm((f) => ({ ...f, checklist: f.checklist.map((x, j) => j === i ? { ...x, text: e.target.value } : x) }))} placeholder="Item" />
                <button onClick={() => setForm((f) => ({ ...f, checklist: f.checklist.filter((_, j) => j !== i) }))} className="text-slate-500 hover:text-red-400"><X size={16} /></button>
              </div>
            ))}
            <Button variant="secondary" className="!py-1.5" onClick={() => setForm((f) => ({ ...f, checklist: [...(f.checklist || []), { text: "", done: false }] }))}><Plus size={14} /> Add item</Button>
          </div>
        </Field>
      </Modal>
    </div>
  );
}
